/**
 * VERA storefront behaviour: countdowns, gallery, bundle selector, gift
 * tiles, sticky purchase bar, lazy how-to GIFs and the video wall.
 * See soapvera/INTERACTIONS.md.
 */
(() => {
  const pad = (n) => String(n).padStart(2, '0');

  /* Looping promo countdown: the same position for every visitor, restarts at zero. */
  function initLoopTimers() {
    const timers = document.querySelectorAll('[data-vera-loop-timer]');
    if (!timers.length) return;
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      timers.forEach((el) => {
        const period = (parseInt(el.dataset.minutes, 10) || 40) * 60;
        const left = period - (now % period);
        el.textContent = `${pad(Math.floor(left / 60))}:${pad(left % 60)}`;
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* Ship countdown to midnight UK time. */
  function initMidnightTimers() {
    const timers = document.querySelectorAll('[data-vera-midnight-timer]');
    if (!timers.length) return;
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    const tick = () => {
      const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
      const elapsed = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
      const left = 86400 - elapsed;
      timers.forEach((el) => {
        el.querySelector('[data-h]').textContent = `${Math.floor(left / 3600)}h`;
        el.querySelector('[data-m]').textContent = `${pad(Math.floor((left % 3600) / 60))}m`;
        el.querySelector('[data-s]').textContent = `${pad(left % 60)}s`;
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* Basket drawer. The basket holds one soap bundle; its stepper moves between
     the bundles by paid soaps (1 = 1 bar, 2 = buy 2 get 1, 3 = buy 3 get 2).
     Free gifts are £0 products kept in step with the soap subtotal: each
     milestone adds its gift, dropping below removes it. */
  const VeraCart = (() => {
    const root = document.querySelector('[data-vera-cart]');
    const cfgEl = document.getElementById('vera-cart-config');
    if (!root || !cfgEl) return null;
    const cfg = JSON.parse(cfgEl.textContent);
    const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const giftIds = { pouch: cfg.pouchId, timer: cfg.timerId };
    const isGift = (item) => item.variant_id === cfg.pouchId || item.variant_id === cfg.timerId;
    const isProtection = (item) => item.variant_id === cfg.protectionId;
    const isExtra = (item) => isGift(item) || isProtection(item);
    // Set on the cart when the customer removes shipping protection.
    const DECLINED = '_shipping_protection_declined';
    const bundles = (cfg.bundles || []).map((b) => ({ ...b, bars: parseInt(b.title, 10) || b.paid }));
    const maxPaid = bundles.length;
    const bundleOf = (variantId) => bundles.find((b) => b.id === Number(variantId));
    const money = (cents) =>
      new Intl.NumberFormat('en-GB', { style: 'currency', currency: cfg.currency || 'GBP' }).format(cents / 100);
    const el = (sel) => root.querySelector(sel);
    const panel = el('.cd__panel');
    let lastFocus = null;

    const api = async (path, body) => {
      const res = await fetch(`${routes}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong');
      return data;
    };

    const soapSubtotal = (cart) =>
      cart.items.filter((i) => !isExtra(i)).reduce((sum, i) => sum + i.original_line_price, 0);

    // Put exactly one bundle in the basket (paid = 0 empties it).
    const setBundle = async (paid) => {
      const updates = {};
      bundles.forEach((b) => (updates[b.id] = b.paid === paid ? 1 : 0));
      await api('cart/update.js', { updates });
      return api('cart.js');
    };

    // Fold stray quantities (e.g. two single bars) into the matching bundle.
    const normalise = async (cart) => {
      const lines = cart.items.filter((i) => bundleOf(i.variant_id));
      const paid = Math.min(maxPaid, lines.reduce((n, i) => n + bundleOf(i.variant_id).paid * i.quantity, 0));
      const tidy = lines.length <= 1 && (!lines[0] || lines[0].quantity === 1);
      return tidy ? cart : setBundle(paid);
    };

    // Add or remove the £0 gifts so they match the milestones reached, and keep
    // shipping protection in any basket with soap unless the customer removed it.
    const syncGifts = async (cart) => {
      const sub = soapSubtotal(cart);
      const updates = {};
      const attributes = {};
      if (cfg.protectionId) {
        const declined = cart.attributes && cart.attributes[DECLINED] === '1';
        const want = sub > 0 && !declined ? 1 : 0;
        const have = cart.items.filter(isProtection).reduce((n, i) => n + i.quantity, 0);
        if (have !== want) updates[cfg.protectionId] = want;
        // An emptied basket starts fresh next time.
        if (sub === 0 && declined) attributes[DECLINED] = '';
      }
      cfg.milestones.forEach((m) => {
        const id = giftIds[m.key];
        if (!id) return;
        const want = sub >= m.at ? 1 : 0;
        const have = cart.items.filter((i) => i.variant_id === id).reduce((n, i) => n + i.quantity, 0);
        if (have !== want) updates[id] = want;
      });
      if (!Object.keys(updates).length && !Object.keys(attributes).length) return cart;
      await api('cart/update.js', { updates, attributes });
      return api('cart.js');
    };

    const image = (item) => {
      if (item.variant_id === cfg.pouchId) return cfg.giftImages.pouch;
      if (item.variant_id === cfg.timerId) return cfg.giftImages.timer;
      const b = bundleOf(item.variant_id);
      return (b && cfg.packImages[b.bars]) || item.image || cfg.packImages['1'];
    };

    const renderProgress = (cart) => {
      const sub = soapSubtotal(cart);
      const goal = cfg.milestones[cfg.milestones.length - 1].at;
      const left = goal - sub;
      el('[data-progress-text]').innerHTML =
        left > 0
          ? `<em>Free shipping</em> is only <em>${money(left)}</em> away`
          : `🎉 You've unlocked <em>free shipping</em>`;
      el('[data-progress-fill]').style.width = `${Math.min(100, (sub / goal) * 100)}%`;
      el('[data-milestones]').innerHTML = cfg.milestones
        .map(
          (m) => `<li class="cd__milestone${sub >= m.at ? ' is-done' : ''}" style="left:${(m.at / goal) * 100}%">
            <img src="${m.img}" alt="" width="40" height="40"><span>${m.label}</span></li>`
        )
        .join('');
    };

    const benefitsTpl = document.getElementById('vera-protect-benefits');
    const benefitsHtml = benefitsTpl ? benefitsTpl.innerHTML : '';
    const confirmEl = el('[data-protect-confirm]');
    const shieldSvg = '<svg viewBox="0 0 24 24"><path d="M12 2.5 4.5 5.5v6c0 4.6 3.2 8.4 7.5 10 4.3-1.6 7.5-5.4 7.5-10v-6L12 2.5Z" fill="currentColor"/><path d="m8.5 12 2.4 2.4 4.6-4.8" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const renderProtection = (item) => `<li class="cd__line cd__line--protect" data-key="${item.key}">
        <span class="cd__shield" aria-hidden="true">
          ${shieldSvg}
        </span>
        <div>
          <p class="cd__name-row">
            <span class="cd__name">Shipping Protection</span>
            <button type="button" class="cd__info" aria-label="What's covered" aria-expanded="false" data-protect-info>i</button>
          </p>
          <div class="cd__tip" hidden data-protect-tip>${benefitsHtml}</div>
          <button type="button" class="cd__remove" data-protection-remove>Remove</button>
        </div>
        <div class="cd__price"><strong>${money(item.final_line_price)}</strong></div>
      </li>`;

    // Shown in place of protection once removed, so it can be added back.
    const renderProtectionOffer = () => `<li class="cd__line cd__line--protect cd__line--offer">
        <span class="cd__shield" aria-hidden="true">${shieldSvg}</span>
        <div>
          <p class="cd__name-row">
            <span class="cd__name">Shipping Protection</span>
            <button type="button" class="cd__info" aria-label="What's covered" aria-expanded="false" data-protect-info>i</button>
          </p>
          <p class="cd__offer-text">Your order isn't protected</p>
          <div class="cd__tip" hidden data-protect-tip>${benefitsHtml}</div>
        </div>
        <button type="button" class="cd__add-protect" data-protect-add>Add ${money(cfg.protectionPrice)}</button>
      </li>`;

    const renderLine = (item) => {
      if (isProtection(item)) return renderProtection(item);
      const gift = isGift(item);
      const compare = (cfg.compareAt[String(item.variant_id)] || 0) * item.quantity;
      const paid = item.final_line_price;
      const save = compare > paid ? compare - paid : 0;
      const b = bundleOf(item.variant_id);
      let variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      if (b) {
        const free = b.bars - b.paid;
        variant = free > 0 ? `${b.paid} soaps + ${free} free (${b.bars} bars)` : `${b.paid} soap`;
      }
      return `<li class="cd__line" data-key="${item.key}">
        <img class="cd__img" src="${image(item)}" alt="" width="76" height="76" loading="lazy">
        <div>
          <p class="cd__name">${item.product_title}</p>
          ${variant ? `<p class="cd__variant">${variant}</p>` : ''}
          ${
            gift
              ? '<span class="cd__free-tag">Free gift</span>'
              : b
                ? `<div class="cd__qty">
                    <button type="button" data-paid="${b.paid - 1}" aria-label="One soap fewer">−</button>
                    <output aria-live="polite">${b.paid}</output>
                    <button type="button" data-paid="${b.paid + 1}" aria-label="One soap more"${b.paid >= maxPaid ? ' disabled' : ''}>+</button>
                  </div>
                  <br><button type="button" class="cd__remove" data-paid="0">Remove</button>`
                : `<button type="button" class="cd__remove" data-qty="0">Remove</button>`
          }
        </div>
        <div class="cd__price">
          ${compare > paid ? `<s>${money(compare)}</s>` : ''}
          <strong>${paid === 0 ? 'FREE' : money(paid)}</strong>
          ${save ? `<small>You save ${money(save)}</small>` : ''}
        </div>
      </li>`;
    };

    const render = (cart) => {
      const soapItems = cart.items.filter((i) => !isExtra(i));
      // Soap first, then gifts, shipping protection always last.
      const items = [...soapItems, ...cart.items.filter(isGift), ...cart.items.filter(isProtection)];
      // Count bars, free ones included.
      const count = soapItems.reduce((n, i) => n + (bundleOf(i.variant_id) ? bundleOf(i.variant_id).bars : 1) * i.quantity, 0);
      el('[data-cart-count]').textContent = count ? `• ${count} ${count === 1 ? 'bar' : 'bars'}` : '';
      const offer = cfg.protectionId && soapItems.length && !cart.items.some(isProtection);
      el('[data-cart-lines]').innerHTML = items.map(renderLine).join('') + (offer ? renderProtectionOffer() : '');
      el('[data-cart-empty]').hidden = soapItems.length > 0;
      el('[data-cart-foot]').hidden = soapItems.length === 0;
      el('[data-cart-progress]').hidden = false;
      el('[data-cart-subtotal]').textContent = money(cart.total_price);
      const saved = items.reduce((sum, i) => {
        const c = (cfg.compareAt[String(i.variant_id)] || 0) * i.quantity;
        return sum + Math.max(0, c - i.final_line_price);
      }, 0);
      const savingsEl = el('[data-cart-savings]');
      savingsEl.hidden = !saved;
      savingsEl.innerHTML = `<span>You're saving</span><strong>${money(saved)}</strong>`;
      renderProgress(cart);
      document.querySelectorAll('.site-header__count').forEach((n) => (n.textContent = count));
      document.querySelectorAll('.site-header__cart').forEach((a) => (a.hidden = count === 0));
    };

    const refresh = async () => {
      root.classList.add('is-busy');
      try {
        const cart = await syncGifts(await normalise(await api('cart.js')));
        render(cart);
        return cart;
      } finally {
        root.classList.remove('is-busy');
      }
    };

    const open = async () => {
      lastFocus = document.activeElement;
      root.hidden = false;
      document.documentElement.classList.add('cd-lock');
      requestAnimationFrame(() => root.classList.add('is-open'));
      panel.focus();
      await refresh();
    };

    const close = () => {
      root.classList.remove('is-open');
      document.documentElement.classList.remove('cd-lock');
      setTimeout(() => {
        root.hidden = true;
      }, 300);
      if (lastFocus) lastFocus.focus();
    };

    // Choosing a bundle on the page replaces whatever bundle is in the basket.
    const add = async (variantId) => {
      const b = bundleOf(variantId);
      if (b) await setBundle(b.paid);
      else await api('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
      await open();
    };

    root.addEventListener('click', async (e) => {
      if (e.target.closest('[data-cart-close]')) return close();
      const info = e.target.closest('[data-protect-info]');
      if (info) {
        const tip = info.closest('.cd__line').querySelector('[data-protect-tip]');
        tip.hidden = !tip.hidden;
        info.setAttribute('aria-expanded', String(!tip.hidden));
        return;
      }
      // Removing protection asks first, listing what they'd lose.
      if (e.target.closest('[data-protection-remove]')) {
        confirmEl.hidden = false;
        el('[data-protect-keep]').focus();
        return;
      }
      if (e.target.closest('[data-protect-keep]') || e.target === confirmEl) {
        confirmEl.hidden = true;
        return;
      }
      if (e.target.closest('[data-protect-add]')) {
        root.classList.add('is-busy');
        try {
          await api('cart/update.js', { updates: { [cfg.protectionId]: 1 }, attributes: { [DECLINED]: '' } });
        } finally {
          await refresh();
        }
        return;
      }
      if (e.target.closest('[data-protect-sure]')) {
        confirmEl.hidden = true;
        root.classList.add('is-busy');
        try {
          await api('cart/update.js', { updates: { [cfg.protectionId]: 0 }, attributes: { [DECLINED]: '1' } });
        } finally {
          await refresh();
        }
        return;
      }
      const btn = e.target.closest('[data-paid], [data-qty]');
      if (!btn || btn.disabled) return;
      root.classList.add('is-busy');
      try {
        if ('paid' in btn.dataset) await setBundle(Math.max(0, Math.min(maxPaid, Number(btn.dataset.paid))));
        else await api('cart/change.js', { id: btn.closest('[data-key]').dataset.key, quantity: 0 });
      } finally {
        await refresh();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || root.hidden) return;
      if (confirmEl && !confirmEl.hidden) confirmEl.hidden = true;
      else close();
    });
    // Header basket icon opens the drawer instead of the /cart page.
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href$="/cart"], .site-header__cart');
      if (!link) return;
      e.preventDefault();
      open();
    });

    return { open, close, add, refresh };
  })();

  /* Gallery: arrows and dots scroll the snap track; the track scroll updates the active dot. */
  function initGallery(root) {
    const track = root.querySelector('[data-track]');
    const dots = [...root.querySelectorAll('.vp__dot')];
    if (!track || !dots.length) return;
    const count = dots.length;
    const current = () => Math.round(track.scrollLeft / track.clientWidth);
    // Arrows wrap round, so the last image leads back to the first.
    const go = (i) => {
      const index = (i + count) % count;
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
    };
    dots.forEach((btn) => btn.addEventListener('click', () => go(Number(btn.dataset.index))));
    root.querySelector('[data-prev]')?.addEventListener('click', () => go(current() - 1));
    root.querySelector('[data-next]')?.addEventListener('click', () => go(current() + 1));
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = current();
        dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      });
    });
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(current() + 1);
      if (e.key === 'ArrowLeft') go(current() - 1);
    });
    // The snap track stops at either end, so a swipe past the last image loops to the first (and back).
    let startX = null;
    let startIndex = 0;
    track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startIndex = current();
    }, { passive: true });
    track.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      startX = null;
      if (dx < -40 && startIndex === count - 1) go(0);
      if (dx > 40 && startIndex === 0) go(count - 1);
    }, { passive: true });
  }

  /* Bundle selector, gift tiles, sticky bar and submit state. */
  function initProduct(root) {
    initGallery(root);
    const form = root.querySelector('[data-vera-form]');
    if (!form) return;
    const radios = [...form.querySelectorAll('input[name="id"]')];
    const giftsInput = form.querySelector('[data-gifts-input]');
    const rewards = {
      pouch: root.querySelector('[data-reward="pouch"]'),
      timer: root.querySelector('[data-reward="timer"]'),
      shipping: root.querySelector('[data-reward="shipping"]'),
    };
    const sticky = document.querySelector('[data-vera-sticky]');
    const cta = form.querySelector('[data-cta]');

    const update = (pushUrl) => {
      const r = radios.find((x) => x.checked) || radios[0];
      if (!r) return;
      Object.entries(rewards).forEach(([key, el]) => {
        if (el) el.classList.toggle('is-locked', r.dataset[key] !== 'true');
      });
      giftsInput.value = r.dataset.gifts || '';
      giftsInput.disabled = !r.dataset.gifts;
      if (pushUrl && r.value) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', r.value);
        history.replaceState(null, '', url);
      }
    };

    // A ?variant= link preselects that bundle.
    const wanted = new URLSearchParams(window.location.search).get('variant');
    const match = wanted && radios.find((r) => r.value === wanted && !r.disabled);
    if (match) match.checked = true;

    radios.forEach((r) => r.addEventListener('change', () => update(true)));
    update(false);

    // With JavaScript, the bundle goes into the basket and the basket drawer
    // opens (it adds the unlocked free gifts). Without it, the plain form
    // still adds the bundle.
    const errorEl = form.querySelector('[data-error]');
    form.addEventListener('submit', async (e) => {
      const r = radios.find((x) => x.checked);
      if (!r || !r.value || !VeraCart) return;
      e.preventDefault();
      cta.disabled = true;
      cta.setAttribute('aria-busy', 'true');
      if (errorEl) errorEl.hidden = true;
      try {
        await VeraCart.add(r.value);
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = `${err.message}. Please try again.`;
          errorEl.hidden = false;
        }
      } finally {
        cta.disabled = false;
        cta.removeAttribute('aria-busy');
      }
    });

    // Re-enable if the visitor comes back with the browser back button.
    window.addEventListener('pageshow', () => {
      cta.disabled = false;
      cta.removeAttribute('aria-busy');
    });

    if (sticky && 'IntersectionObserver' in window) {
      sticky.querySelector('[data-sticky-btn]').addEventListener('click', () => {
        if (form.requestSubmit && !cta.disabled) form.requestSubmit(cta);
      });
      new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(cta);
    }
  }

  /* How-to GIFs load only when their accordion is first opened. */
  function initLazyGifs() {
    document.querySelectorAll('details[data-lazy-gifs]').forEach((d) => {
      d.addEventListener(
        'toggle',
        () => {
          if (!d.open) return;
          d.querySelectorAll('img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          });
        },
        { once: false }
      );
    });
  }

  /* Video wall: muted autoplay preview while in view. The centre play button
     restarts the video from 0:00 with sound; only one plays with sound at a time. */
  function initVideos() {
    const wall = document.querySelector('[data-vera-videos]');
    if (!wall) return;
    const cards = [...wall.querySelectorAll('.vw__card')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const inView = new Set();

    // Back to the silent looping preview (or paused, if off screen or reduced motion).
    const toPreview = (card) => {
      const video = card.querySelector('video');
      card.classList.remove('is-playing');
      video.muted = true;
      video.loop = true;
      if (!reduceMotion && inView.has(card)) video.play().catch(() => {});
      else video.pause();
    };

    const playWithSound = (card) => {
      cards.forEach((c) => c !== card && toPreview(c));
      const video = card.querySelector('video');
      video.currentTime = 0;
      video.muted = false;
      video.loop = false;
      card.classList.add('is-playing');
      video.play().catch(() => toPreview(card));
    };

    cards.forEach((card) => {
      const video = card.querySelector('video');
      card.querySelector('.vw__play').addEventListener('click', () => playWithSound(card));
      // Tapping a video that is playing with sound stops it and brings the button back.
      video.addEventListener('click', () => {
        if (card.classList.contains('is-playing')) toPreview(card);
        else playWithSound(card);
      });
      video.addEventListener('ended', () => toPreview(card));
    });

    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) inView.add(target);
          else inView.delete(target);
          if (!target.classList.contains('is-playing') || !isIntersecting) toPreview(target);
        });
      },
      { threshold: 0.5 }
    );
    cards.forEach((c) => io.observe(c));
  }

  const start = () => {
    initLoopTimers();
    initMidnightTimers();
    document.querySelectorAll('[data-vera-product]').forEach(initProduct);
    initLazyGifs();
    initVideos();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
